import {
  Box,
  Center,
  chakra,
  Flex,
  FlexProps,
  Grid,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import React, { useMemo, useRef, useState } from "react";
import { LabelText } from "../styleComponents/Texts";
import { genBlock } from "@/utils/bem";
import { searchFilter as wordTableSearchFilter } from "./utils";
import {
  Categories,
  KeywordsJSONType,
  WordEntityTypeWithParentKeys,
  WordNameDictionary,
} from "./type";
import { useFetchJson } from "@/hooks/useFetchJson";
import { WordTableCell } from "./TopLevelCell";
import { Search } from "@/components/Search";
import { themeStore } from "@/stores/theme";
import getScrollBarProps from "@/styles/scrollBarProps";
import { WordTableContext } from "./context";
import { langStore, translate } from "@noli/business";
import { Editor_Words } from "@/utils/lang/editor";
import { WithClassName } from "@/types/base";
import classNames from "classnames";
import { WordTableNames, wordTableStore } from "./store";
import { replaceAll } from "@/utils/string";

interface WordTableProps {
  sourceUrl: string;
  text?: string;
  setText?: (text: string) => void;
  title?: string;
  tablePopupProps?: FlexProps;
  onSelect?: (record: string) => void;
  tableRows?: number;
  tableTitle?: Editor_Words;
  searchPlaceholder?: Editor_Words;
  defaultCategory?: Categories;
  tableName: WordTableNames;
  searchFilter?: (
    list: WordEntityTypeWithParentKeys[],
    names: WordNameDictionary,
  ) => (value?: string) => WordEntityTypeWithParentKeys[];
}
const block = genBlock("c-word-table");
const IWordTable: React.FC<WithClassName<WordTableProps>> = ({
  text = "",
  setText,
  title,
  sourceUrl,
  tablePopupProps,
  onSelect,
  tableTitle,
  searchPlaceholder,
  defaultCategory,
  className,
  tableRows = 1,
  tableName,
  searchFilter = wordTableSearchFilter,
}) => {
  const lang = langStore.tgt;
  const { selectColor, selectBgColor, selectHoverColor } = themeStore.styles;
  const minH = useMemo(() => tableRows * 34, [tableRows]);

  useFetchJson<KeywordsJSONType>(sourceUrl, { useCache: true }, (json) => {
    wordTableStore.setKeywordsDataset(tableName, json);
  });
  const dataset = wordTableStore[tableName];
  const { data: dataWithParentKeys, nameDict, searchList: listForSearch } = dataset;
  // const { data = [], dictionary: nameDict = {} } = json ?? {};
  // const dataWithParentKeys = useMemo(() => formatTableData(data), [data]);
  // const listForSearch = useMemo(() => getTableForSearch(data), [data]);

  const [category, setCategory] = useState(defaultCategory);
  const [isSearchExpand, setIsSearchExpand] = useState(false);
  const [searchResult, setSearchResult] = useState<WordEntityTypeWithParentKeys[] | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const checkValueFn = (word: WordEntityTypeWithParentKeys) => {
    const words = replaceAll(text, "，", ",")
      .split(",")
      .map((word) => word.trim());
    const isChecked = words?.includes(word.value);
    return isChecked;
  };

  const handleSearch = (value?: string) => {
    const filtered = searchFilter?.(listForSearch, nameDict)(value);
    setSearchResult(filtered);
    setIsSearchExpand(true);
    searchRef.current?.focus();
  };

  function handleSelect(word: WordEntityTypeWithParentKeys) {
    if (text) {
      const words = replaceAll(text, "，", ",")
        .split(",")
        .map((str) => str.trim());
      const isChecked = words?.includes(word.value);
      if (isChecked) {
        const wordIndex = words.indexOf(word.value);
        words.splice(wordIndex, 1);
        const newStr = words.join(", ");
        setText?.(newStr);
      } else {
        words.push(word.value);
        const newStr = words.join(", ");
        setText?.(newStr);
      }
    } else {
      setText?.(word.value);
    }

    const keyStr = word.parentKeys.concat().reverse().concat(word.key).join(" | ");
    onSelect?.(keyStr);
  }

  return (
    <Flex flexDirection="column" w="100%" className={classNames(block(), className)}>
      {title ? <LabelText mb="16px">{title}</LabelText> : null}
      <WordTableContext.Provider
        value={{
          onSelect: handleSelect,
          tablePopupProps,
          nameDict,
          tableTitle,
          searchPlaceholder,
          category,
          onCategoryChange: setCategory,
          defaultCate: defaultCategory,
          checkValueFn,
          searchFilter,
        }}
      >
        <Box pos="relative" w="100%" className={block({ e: "search-container" })}>
          <Menu isOpen={isSearchExpand} onClose={() => setIsSearchExpand(false)}>
            {({ isOpen }) => {
              return (
                <>
                  <MenuButton
                    mb="12px"
                    h="28px"
                    w="100%"
                    display="block"
                    outline="none"
                    cursor="default"
                    onFocus={() => {
                      searchRef.current?.focus();
                    }}
                  />
                  <Search
                    ref={searchRef}
                    pos="absolute"
                    zIndex={1}
                    onSearch={handleSearch}
                    top={0}
                    placeholder={searchPlaceholder ? translate(searchPlaceholder, lang) : undefined}
                  />
                  {isOpen ? (
                    <MenuList
                      maxH="300px"
                      bgColor={selectBgColor}
                      borderColor={selectBgColor}
                      fontSize="12px"
                      zIndex={10}
                      onFocus={(e) => {
                        if (searchRef.current && e.relatedTarget === searchRef.current) {
                          searchRef.current?.focus();
                        }
                      }}
                      {...getScrollBarProps()}
                    >
                      {searchResult?.length ? (
                        <>
                          {searchResult.map((item) => {
                            const isChecked = text.indexOf(item.value) > -1;
                            return (
                              <MenuItem
                                display="flex"
                                flexDirection="column"
                                alignItems="flex-start"
                                _hover={{
                                  bgColor: selectHoverColor,
                                }}
                                _focus={{
                                  bgColor: "transparent",
                                }}
                                color={isChecked ? "brand.primary" : selectColor}
                                closeOnSelect={false}
                                onClick={() => handleSelect(item)}
                                key={item.key}
                              >
                                {lang === "zh" ? <Text>{nameDict[item.key]?.zh}</Text> : null}
                                <Text>{nameDict[item.key]?.en}</Text>
                              </MenuItem>
                            );
                          })}
                        </>
                      ) : (
                        <Center color={selectColor} _hover={{ color: selectColor }} h="70px">
                          {translate(Editor_Words["search-not-found"], lang)}
                        </Center>
                      )}
                    </MenuList>
                  ) : null}
                </>
              );
            }}
          </Menu>
        </Box>
        <Grid
          templateColumns="repeat(3, 33.3%)"
          className={block({ e: "table" })}
          minH={`${minH}px`}
          rounded="4px"
          border="1px solid"
          borderColor="text.secondary"
        >
          {dataWithParentKeys.map((word) => {
            return <WordTableCell key={word.key} data={word} />;
          })}
        </Grid>
      </WordTableContext.Provider>
    </Flex>
  );
};

export const WordTable = chakra(IWordTable);
